import { DialogBoundary, DialogTrigger } from "@suse-o11y/dialog";
import { Permitted } from "@suse-o11y/permitted";
import { prop } from "@suse-o11y/prop";
import { useUnmount } from "@suse-o11y/use-unmount";
import React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { type QueryView, Type, type Version, isNodeOwned } from "~/models/dto";

import { RightPaneState } from "../../app/state/right-pane/RightPaneState";
import {
	useCanDeleteCurrentView,
	useCanEditCurrentView,
	useCanResetCurrentQuery,
	useCanSaveCurrentView,
	useCanSaveCurrentViewAs,
} from "../../app/state/view/viewpoint/CanDoHooks";
import { createSaveAsView } from "../../app/state/view/viewpoint/ViewPointHelpers";
import {
	useCurrentInitialView,
	useCurrentView,
	useIsCurrentViewDirty,
} from "../../app/state/view/viewpoint/ViewPointHooks";
import { selectIsExploreSubview } from "../../app/state/view/viewpoint/ViewPointSelectors";
import { Messages } from "../../common/Messages";
import { ViewFormDrawer } from "../../components/_drawer/_view-form";
import { Link, useNavigateFrom } from "../../components/react/link";
import { DONT_AUTO_DISMISS, notification } from "../../components/react/notification/Notification";
import { type SaveAsButtonAction, SaveButton } from "../../components/react/save/SaveButton";
import { usePersistNodeFlow } from "../../queries";
import { queryPerspectives } from "../../queries/perspectives";
import { useQuery } from "../../queries/react-query";
import { queryUIConfig } from "../../queries/ui-config";

export const SaveViewButton: React.FC<{
	slug: string;
}> = ({ slug }) => {
	const isExploreSubview = useSelector(selectIsExploreSubview);
	const { data: perspectives } = useQuery({
		...queryPerspectives(slug),
		staleTime: Number.POSITIVE_INFINITY,
	});

	// If it's an explore view we shouldn't bail early since they should always show the save/save as buttons
	if (!isExploreSubview && (perspectives == null || perspectives.readOnly)) {
		return null;
	}

	return <SaveViewButtonContent key={slug} slug={slug} />;
};

const SaveViewButtonContent: React.FC<{
	slug: string;
}> = ({ slug }) => {
	const { pathname, search } = useLocation();
	const enableReset = useCanResetCurrentQuery();
	const enableSaveAs = useCanSaveCurrentViewAs();
	const enableSave = useCanSaveCurrentView();
	const currentViewWithChanges = useCurrentView();
	const isDirty = useIsCurrentViewDirty();
	const enableEdit = useCanEditCurrentView();
	const enableDelete = useCanDeleteCurrentView();
	const currentView = useCurrentInitialView();
	const navigateFrom = useNavigateFrom();
	const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
	const [isSaveAsDrawerOpen, setIsSaveAsDrawerOpen] = React.useState(false);
	const [isEditDrawerOpen, setIsEditDrawerOpen] = React.useState(false);

	const unsavedViewNotificationKey = `unsaved-view-${slug}`;

	const { data: defaultQueryVersion = "0.0.0" as Version } = useQuery({
		...queryUIConfig,
		select: prop("queryVersion"),
	});

	const { mutate: mutateView } = usePersistNodeFlow(Type.QueryView, "view");
	const viewWithChanges = currentViewWithChanges.getOrElse(undefined);

	React.useEffect(() => {
		notification.close(unsavedViewNotificationKey);
	}, [unsavedViewNotificationKey]);

	useUnmount(() => {
		if (isDirty.getOrElse(false) && viewWithChanges != null) {
			notification.warning({
				key: unsavedViewNotificationKey,
				duration: DONT_AUTO_DISMISS,
				message: (
					<UnsavedViewNotification
						backUrl={`${pathname}${search}`}
						defaultQueryVersion={defaultQueryVersion}
						enableSave={enableSave}
						enableSaveAs={enableSaveAs}
						notificationKey={unsavedViewNotificationKey}
						viewWithChanges={viewWithChanges}
					/>
				),
			});
		}
	});

	return Permitted.all({
		view: currentView,
		viewWithChanges: currentViewWithChanges,
	}).match({
		denied: () => null,

		granted: ({ value: { view, viewWithChanges } }) => (
			<SaveButton
				isDropdownOpen={isDropdownOpen}
				onDropdownOpen={setIsDropdownOpen}
				reset={{
					enabler: enableReset,
					title: "Reset view",
					onSelect: () => {
						navigateFrom("*", {
							search: (search) => ({
								...search,
								rightPane: RightPaneState.resetWhenVisible(search.rightPane),
								view: undefined,
								query: undefined,
								// reset detached filters for locked views
								detachedFilters: isNodeOwned(view) ? undefined : search.detachedFilters,
							}),
						});
					},
				}}
				rest={[
					{
						enabler: enableEdit && viewWithChanges,
						title: "Edit view properties",
						active: isEditDrawerOpen,
						onSelect: () => {
							setIsEditDrawerOpen(true);
						},
						trigger: (original, children) => (
							<DialogBoundary onOpenChange={setIsEditDrawerOpen} open={isEditDrawerOpen}>
								<DialogTrigger asChild>{children}</DialogTrigger>

								<ViewFormDrawer initial={original} />
							</DialogBoundary>
						),
					} satisfies SaveAsButtonAction<QueryView>,
					{
						enabler: enableDelete,
						title: "Delete view",
						onSelect: () => {
							void mutateView("delete", view, Messages.deleteConfirm);
						},
					},
				]}
				save={{
					isPrimary: true,
					enabler: enableSave,
					title: "Save view",
					onSelect: () => {
						void mutateView("save", viewWithChanges);
					},
				}}
				saveAs={
					{
						enabler: enableSaveAs && viewWithChanges,
						title: "Save view as...",
						active: isSaveAsDrawerOpen,
						onSelect: () => {
							setIsSaveAsDrawerOpen(true);
						},
						trigger: (original, children) => (
							<DialogBoundary onOpenChange={setIsSaveAsDrawerOpen} open={isSaveAsDrawerOpen}>
								<DialogTrigger asChild>{children}</DialogTrigger>

								<ViewFormDrawer initial={createSaveAsView(original, defaultQueryVersion)} />
							</DialogBoundary>
						),
					} satisfies SaveAsButtonAction<QueryView>
				}
			/>
		),
	});
};

const UnsavedViewNotification: React.FC<{
	notificationKey: string;
	backUrl: string;
	enableSave: boolean;
	enableSaveAs: boolean;
	viewWithChanges: QueryView;
	defaultQueryVersion: Version;
}> = ({ notificationKey, backUrl, enableSave, enableSaveAs, viewWithChanges, defaultQueryVersion }) => {
	const { mutate: mutateUnsavedView } = usePersistNodeFlow(Type.QueryView, "view", undefined, (_error, view) => {
		notification.error({
			key: notificationKey,
			duration: DONT_AUTO_DISMISS,
			message: (
				<div>
					Saving the view failed.{" "}
					<button
						className="link link-secondary"
						onClick={() => {
							mutateUnsavedView("save", view);
							notification.close(notificationKey);
						}}
						type="button"
					>
						Retry
					</button>
					.
				</div>
			),
		});
	});

	return (
		<div>
			You have unsaved changes in the{" "}
			<Link theme="secondary" to={backUrl}>
				{viewWithChanges.name}
			</Link>{" "}
			view.
			{enableSave ? (
				<>
					{" "}
					<button
						className="link link-secondary"
						onClick={() => {
							mutateUnsavedView("save", viewWithChanges);
							notification.close(notificationKey);
						}}
						type="button"
					>
						Save changes
					</button>
					.
				</>
			) : enableSaveAs ? (
				<>
					{" "}
					<DialogBoundary>
						<DialogTrigger className="link link-secondary">Save changes</DialogTrigger>

						<ViewFormDrawer initial={createSaveAsView(viewWithChanges, defaultQueryVersion)} />
					</DialogBoundary>
					.
				</>
			) : null}
		</div>
	);
};
